import { ChatPersonModel } from '../models/chat.model';
import { MentionableText, Mention, MentionTrigger, MentionUser } from '../models/mention.model';

export type TextSegment = { kind: 'text'; text: string } | { kind: 'mention'; text: string; userId: Guid };

const TRIGGER_PATTERN = /(?<![\p{L}\p{N}_])@([\p{L}\p{N}_\-']*)$/u;

export function toMentionUser(person: ChatPersonModel): MentionUser {
  return {
    userId: person.userId,
    displayName: person.displayName,
    avatarUrl: person.profilePicture || undefined,
  };
}

export class MentionHelper {
  public static get empty(): MentionableText {
    return { body: '', mentions: [] };
  }

  public static readonly maxPickerResults = 6;

  public static filterCandidates(users: MentionUser[], query: string): MentionUser[] {
    const needle = query.toLowerCase();

    return users
      .filter((user) => user.displayName.toLowerCase().includes(needle))
      .slice(0, MentionHelper.maxPickerResults);
  }

  public static detectTrigger(body: string, caret: number): MentionTrigger | undefined {
    const upToCaret = body.slice(0, caret);
    const match = TRIGGER_PATTERN.exec(upToCaret);

    if (!match) {
      return undefined;
    }

    const query = match[1];
    const triggerStart = caret - query.length - 1;

    return { query, triggerStart, caret };
  }

  public static insertMention(
    current: MentionableText,
    trigger: MentionTrigger,
    user: MentionUser
  ): { next: MentionableText; nextCaret: number } {
    const insertedText = `@${user.displayName}`;
    const before = current.body.slice(0, trigger.triggerStart);
    const after = current.body.slice(trigger.caret);
    const needsSpace = !after.startsWith(' ');
    const trailing = needsSpace ? ' ' : '';
    const body = `${before}${insertedText}${trailing}${after}`;

    const shift = insertedText.length + trailing.length - (trigger.caret - trigger.triggerStart);
    const shiftedExisting = current.mentions
      .filter((mention) => mention.start >= trigger.caret || mention.start + mention.length <= trigger.triggerStart)
      .map((mention) => {
        if (mention.start >= trigger.caret) {
          return { ...mention, start: mention.start + shift };
        }

        return mention;
      });

    const newMention: Mention = {
      userId: user.userId,
      start: trigger.triggerStart,
      length: insertedText.length,
    };

    return {
      next: { body, mentions: [...shiftedExisting, newMention].sort((a, b) => a.start - b.start) },
      nextCaret: trigger.triggerStart + insertedText.length + trailing.length,
    };
  }

  public static reconcile(previous: MentionableText, newBody: string): MentionableText {
    const prefixLength = MentionHelper.commonPrefixLength(previous.body, newBody);
    const suffixLength = MentionHelper.commonSuffixLength(previous.body, newBody, prefixLength);
    const shift = newBody.length - previous.body.length;
    const tailStart = previous.body.length - suffixLength;

    const survivors: Mention[] = [];

    for (const mention of previous.mentions) {
      const end = mention.start + mention.length;

      if (end <= prefixLength) {
        survivors.push(mention);
      } else if (mention.start >= tailStart) {
        survivors.push({ ...mention, start: mention.start + shift });
      }
    }

    return { body: newBody, mentions: survivors };
  }

  private static commonPrefixLength(a: string, b: string): number {
    const max = Math.min(a.length, b.length);
    let i = 0;

    while (i < max && a[i] === b[i]) {
      i++;
    }

    return i;
  }

  private static commonSuffixLength(a: string, b: string, prefixLength: number): number {
    const max = Math.min(a.length, b.length) - prefixLength;
    let i = 0;

    while (i < max && a[a.length - 1 - i] === b[b.length - 1 - i]) {
      i++;
    }

    return i;
  }

  public static serialize(value: MentionableText): string {
    const sorted = [...value.mentions].sort((a, b) => a.start - b.start);
    let cursor = 0;
    let out = '';

    for (const mention of sorted) {
      out += value.body.slice(cursor, mention.start);

      const display = value.body.slice(mention.start + 1, mention.start + mention.length);

      out += `@[${display}](${mention.userId})`;
      cursor = mention.start + mention.length;
    }

    out += value.body.slice(cursor);

    return out;
  }

  public static parse(serialized: string | null): MentionableText {
    if (serialized === null) {
      return MentionHelper.empty;
    }

    const pattern = /@\[([^\]]+)]\(([^)]+)\)/g;
    const mentions: Mention[] = [];
    let body = '';
    let cursor = 0;

    for (const match of serialized.matchAll(pattern)) {
      const [matched, display, userId] = match;
      const matchStart = match.index;

      body += serialized.slice(cursor, matchStart);

      const start = body.length;
      const insertedText = `@${display}`;

      body += insertedText;

      mentions.push({ userId, start, length: insertedText.length });

      cursor = matchStart + matched.length;
    }

    body += serialized.slice(cursor);

    return { body, mentions };
  }

  public static toSegments(value: MentionableText): TextSegment[] {
    const segments: TextSegment[] = [];
    const sorted = [...value.mentions].sort((a, b) => a.start - b.start);
    let cursor = 0;

    for (const mention of sorted) {
      if (mention.start > cursor) {
        segments.push({ kind: 'text', text: value.body.slice(cursor, mention.start) });
      }

      segments.push({
        kind: 'mention',
        text: value.body.slice(mention.start, mention.start + mention.length),
        userId: mention.userId,
      });

      cursor = mention.start + mention.length;
    }

    if (cursor < value.body.length) {
      segments.push({ kind: 'text', text: value.body.slice(cursor) });
    }

    return segments;
  }
}
